import { useEffect, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import BaseTextArea from 'components/atoms/base-textarea';
import BaseButton from 'components/atoms/baseButton';
import ChevronLeftIcon from 'components/atoms/icons/chevronLeft';
import { usePatientActivity } from 'infrastructure/hooks/rpm/activity/use-patient-activity';
import BaseLoader from 'components/atoms/base-loader';
import {
  type ActivityStatus,
  type ReadingTemplateRoles,
} from 'infrastructure/enums';
import { getReadingTypeLabel } from 'infrastructure/functions';
import MasterModal from 'components/organisms/masterModal';

import s from './styles.module.scss';
import TemplateList from './components/template-list';
import Template from './components/template';
import ReadingTypeList from './components/reading-type-list';
import Timer from './components/timer';
import { useTemplatesLogic } from './use-logic';

interface PatientReadingTemplateProps {
  templateRole: ReadingTemplateRoles;
  open: boolean;
  patientGuid: string;
  type: ActivityStatus;
  onReadingsRefetch?: () => void;
  refetchActivity?: () => void;
  onClose: () => void;
}

const PatientReadingTemplateModal: FC<PatientReadingTemplateProps> = ({
  templateRole,
  open,
  patientGuid,
  type,
  onReadingsRefetch,
  refetchActivity,
  onClose,
}) => {
  const { activity, isLoading } = usePatientActivity({
    patientGuid,
    type,
    pageSize: 10,
    onReadingsRefetch,
  });

  const {
    aggData: { flatData, groupedDataByReadingType },
    completedList,
    currentReadingType,
    setCurrentReadingType,
    output,
    setOutput,
    formWrapperRef,
    timerRef,
    handleSaveCurrentForm,
    formik,
    handleSubmit,
    reviewLoading,
    Alert,
    MonthlyInteractionAlert,
  } = useTemplatesLogic({
    activity,
    templateRole,
    patientGuid,
    onReadingsRefetch,
    refetchActivity,
  });

  const { t } = useTranslation();

  const onSave = async () => {
    await formik.submitForm();
    await handleSubmit();
    onClose();
  };

  const completedCount = completedList.length;
  const totalCount = flatData.length;
  const isAllCompleted = completedCount === totalCount;

  const initTitle = t('labels.bulkReadingsReply');
  const title = currentReadingType ? (
    <div className={s['modal-title']}>
      <BaseButton
        type="secondary"
        icon={<ChevronLeftIcon />}
        onClick={() => setCurrentReadingType(null)}
      />
      <p>
        {initTitle} {getReadingTypeLabel(currentReadingType.readingType)}
      </p>
    </div>
  ) : (
    initTitle
  );

  const modalFooter = (
    <div className={s.footer}>
      {currentReadingType === null ? (
        <>
          <BaseButton
            type="secondary"
            label={t('controls.cancel')}
            onClick={onClose}
            width={130}
          />
          <BaseButton
            label={t('controls.sendBulkReply')}
            onClick={onSave}
            width={130}
            disabled={!isAllCompleted}
            loading={reviewLoading}
          />
        </>
      ) : (
        <BaseButton
          label={t('controls.save')}
          onClick={() =>
            handleSaveCurrentForm(
              currentReadingType.readingType,
              currentReadingType.globalStatus,
            )
          }
          width={130}
        />
      )}
    </div>
  );

  useEffect(() => {
    if (open) timerRef.current?.startTracker();
    else timerRef.current?.deleteTracker();
  }, [open]);

  return (
    <>
      <MasterModal
        title={title}
        rightTitle={<Timer useTrackerRef={timerRef} />}
        isOpen={open}
        toggle={onClose}
        modalClassName={s.modal}
        bodyClassName={s['modal-body']}
        footer={modalFooter}
      >
        {isLoading ? (
          <BaseLoader loading className={s.loader} />
        ) : (
          <div className={s.wrapper}>
            <div className={s['info-list-wrapper']}>
              {currentReadingType === null ? (
                <TemplateList
                  data={flatData}
                  completedTemplates={completedList}
                  setCurrentReadingType={setCurrentReadingType}
                  disable={reviewLoading}
                />
              ) : (
                <ReadingTypeList
                  readingsByType={
                    groupedDataByReadingType[currentReadingType.readingType]
                  }
                />
              )}
            </div>
            {currentReadingType === null ? (
              <div className={s['output-wrapper']}>
                <p>{t('labels.output')}</p>
                <BaseTextArea
                  rows={20}
                  value={output}
                  onChange={setOutput}
                  disabled={completedCount === 0 || reviewLoading}
                />
              </div>
            ) : (
              <div className={s['template-wrapper']} ref={formWrapperRef}>
                <Template
                  currentReadingType={currentReadingType}
                  templateRole={templateRole}
                  formik={formik}
                />
              </div>
            )}
          </div>
        )}
      </MasterModal>
      {Alert}
      {MonthlyInteractionAlert}
    </>
  );
};
export default PatientReadingTemplateModal;
